import React, { useState } from 'react';
import { visionCall } from './VisualSafetyShared';

/** Per-site rules: switch each on or off and tune how long and how confidently something must be seen. */
export const RulesEditor = ({ rules, isDefault, onSaved }) => {
  const [draft, setDraft] = useState(rules);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const change = (id, version, patch) => setDraft((current) => current.map(
    (rule) => (rule.id === id && rule.version === version ? { ...rule, ...patch } : rule)));
  const save = async () => {
    setBusy(true);
    setMessage('');
    try {
      await visionCall('/rules', { method: 'PUT', body: draft });
      setMessage('Rules saved for this site.');
      if (onSaved) await onSaved();
    } catch (err) {
      setMessage(err.message || 'Could not save the rules.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-label="Rules" className="mt-4">
      {isDefault && (
        <p className="mb-3 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-950">
          These are the starter rules. The procedure names are placeholders until your food-safety lead approves the real
          ones. Saving makes them this site's own.
        </p>
      )}
      <ul className="divide-y divide-gray-200 border border-gray-200 bg-white">
        {draft.map((rule) => (
          <li key={`${rule.id}-${rule.version}`} className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <p className="text-sm font-medium text-gray-900">{rule.title}</p>
              <p className="text-xs text-gray-600">{rule.kind} in {rule.zone_types.join(', ')} zones (v{rule.version})</p>
            </div>
            <label className="text-sm text-gray-700">
              <input type="checkbox" checked={rule.enabled} aria-label={`${rule.title} enabled`}
                onChange={(e) => change(rule.id, rule.version, { enabled: e.target.checked })} /> On
            </label>
            <label className="text-sm text-gray-700">Seen for (s)
              <input type="number" min="0" value={rule.min_duration_s} aria-label={`${rule.title} seconds`}
                className="mt-1 block h-9 w-full border border-gray-300 px-2"
                onChange={(e) => change(rule.id, rule.version, { min_duration_s: Number(e.target.value) })} />
            </label>
            <label className="text-sm text-gray-700">Confidence
              <input type="number" min="0" max="1" step="0.05" value={rule.min_confidence} aria-label={`${rule.title} confidence`}
                className="mt-1 block h-9 w-full border border-gray-300 px-2"
                onChange={(e) => change(rule.id, rule.version, { min_confidence: Number(e.target.value) })} />
            </label>
            <label className="text-sm text-gray-700">Procedure
              <input type="text" value={rule.sop_ref} aria-label={`${rule.title} procedure`}
                className="mt-1 block h-9 w-full border border-gray-300 px-2"
                onChange={(e) => change(rule.id, rule.version, { sop_ref: e.target.value })} />
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center gap-3">
        <button type="button" onClick={save} disabled={busy}
          className="h-10 border border-gray-900 bg-gray-900 px-4 text-sm text-white disabled:opacity-50">Save rules</button>
        {message && <span role="status" className="text-sm text-gray-700">{message}</span>}
      </div>
    </section>
  );
};

/** Cameras and zones as JSON, until a drawing tool is justified (zone types: raw, ready_to_eat, wet, storage, aisle, dispatch, prep). */
export const LayoutEditor = ({ layout, zoneTypes, onSaved }) => {
  const [text, setText] = useState(() => JSON.stringify({ cameras: layout.cameras || {} }, null, 2));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const save = async () => {
    let body;
    try { body = JSON.parse(text); } catch { setMessage('That is not valid JSON.'); return; }
    setBusy(true);
    setMessage('');
    try {
      await visionCall('/layout', { method: 'PUT', body });
      setMessage('Layout saved for this site.');
      if (onSaved) await onSaved();
    } catch (err) {
      setMessage(err.message || 'Could not save the layout.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-label="Cameras and zones" className="mt-4">
      <p className="text-sm text-gray-600">
        Each camera has zones, and each zone has a type: {(zoneTypes || []).join(', ')}. Rules only apply in the zone
        types they name.
      </p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} aria-label="Cameras and zones JSON" rows={14}
        className="mt-3 block w-full border border-gray-300 p-2 font-mono text-xs" />
      <div className="mt-3 flex items-center gap-3">
        <button type="button" onClick={save} disabled={busy}
          className="h-10 border border-gray-900 bg-gray-900 px-4 text-sm text-white disabled:opacity-50">Save layout</button>
        {message && <span role="status" className="text-sm text-gray-700">{message}</span>}
      </div>
    </section>
  );
};
